/**
 * Los equipos que nadie vino a buscar.
 *
 * POR QUE ESTO ES UNA ALERTA Y NO UNA COLUMNA MAS
 * ----------------------------------------------
 * Un equipo terminado que sigue en el mostrador es dos problemas a la vez:
 * ocupa lugar y es responsabilidad del local mientras este ahi. A diferencia del
 * resto del tablero, aca no hay nada que hacer con el equipo —el trabajo ya
 * esta— sino con el TELEFONO: hay que llamar. Por eso el numero de contacto no
 * esta escondido en la ficha y cada linea tiene el tel a un toque.
 *
 * El umbral son los mismos siete dias de `v_ot_sin_retirar`, y se cuentan desde
 * que el equipo ENTRO, no desde que quedo listo: la base no guarda cuando se
 * marco LISTO. Es la lectura conservadora y es la que ya usa el backend.
 */
import { PhoneCall, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

import {
  DIAS_SIN_RETIRAR,
  ETIQUETA_ESTADO,
  TONO_ESTADO,
  diasEnTaller,
  nombreCliente,
  nombreOrden,
  textoDias,
  type OrdenTaller,
} from "./logica";

export interface SinRetirarProps {
  ordenes: ReadonlyArray<OrdenTaller>;
  /** Telefono del cliente, si la ficha lo tiene. Se busca por `cliente_id`. */
  telefonoDe: (clienteId: number | null) => string | null;
  onAbrir: (id: number) => void;
}

export function SinRetirar({ ordenes, telefonoDe, onAbrir }: SinRetirarProps): JSX.Element | null {
  if (ordenes.length === 0) return null;

  const titulo =
    ordenes.length === 1
      ? "Hay un equipo terminado sin retirar"
      : `Hay ${ordenes.length} equipos terminados sin retirar`;

  return (
    <Card as="section" padding="normal" acento="warning" aria-label={titulo} className="space-y-2">
      <TituloSeccion
        nivel="seccion"
        icono={TriangleAlert}
        descripcion={`Más de ${DIAS_SIN_RETIRAR} días en el local`}
        className="[&_svg]:text-warning"
      >
        {titulo}
      </TituloSeccion>

      <ul className="divide-y divide-border">
        {ordenes.map((o) => {
          const telefono = telefonoDe(o.cliente_id);
          const dias = diasEnTaller(o);

          return (
            <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              {/* Dos lineas adentro de un ghost: se suelta el alto fijo. */}
              <Button
                type="button"
                variant="ghost"
                onClick={() => onAbrir(o.id)}
                className="h-auto min-w-0 flex-1 flex-col items-start gap-0 px-2 py-1 text-left font-normal"
              >
                <span className="block w-full truncate font-medium">{nombreCliente(o)}</span>
                <span className="block w-full truncate text-xs text-muted-foreground">
                  {o.equipo} · <Codigo>{nombreOrden(o)}</Codigo>
                </span>
              </Button>

              <Badge variant={TONO_ESTADO[o.estado]} className="shrink-0">
                {ETIQUETA_ESTADO[o.estado]}
              </Badge>

              <span
                className={cn(
                  "shrink-0 whitespace-nowrap text-xs font-medium tabular-nums",
                  dias > 30 ? "text-destructive" : "text-warning",
                )}
              >
                hace {textoDias(dias)}
              </span>

              {telefono ? (
                <Button asChild variant="ghost" size="sm" className="shrink-0 tabular-nums">
                  <a href={`tel:${telefono.replace(/\s/g, "")}`}>
                    <PhoneCall aria-hidden="true" />
                    {telefono}
                  </a>
                </Button>
              ) : (
                <span className="shrink-0 text-xs text-muted-foreground">sin teléfono</span>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
